/**
 * PaymentGateway
 *
 * Handle listing activation for free listings
 *
 * @returns onChargeAndActivateListingResponse as ChargeAndActivateListing
 *
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyActivateWebApiHandler_activate } from '@zg-rentals/rental-manager-env';

import { Alert, Heading, Modal } from '@zillow/constellation-10';

import SingleFamilyEnterpriseActions from '../../actions/SingleFamilyEnterpriseActions';

import ErrorMessageConstants from '../../constants/ErrorMessageConstants';
import { ACTIVATE_LISTING_MUTATION_KEY } from '../../constants/QueryConstants';

import PayAndPublishCSEvents from '../../model/ClickstreamEvent/PayAndPublishCSEvents';
import ChargeAndActivateListing from '../../model/ChargeAndActivateListing';
import PreActivationModal from '../Modal/PreActivationModal';

const propTypes = {
  showPreActivationModal: PropTypes.bool,
  updateShowPreActivationModal: PropTypes.func,
  onChargeAndActivateListingResponse: PropTypes.func,
  onPreActivationModalSubmit: PropTypes.func,
};

const defaultProps = {
  showPreActivationModal: false,
  updateShowPreActivationModal: () => {},
  onChargeAndActivateListingResponse: () => {},
  onPreActivationModalSubmit: null,
};

const PaymentGateway = ({
  showPreActivationModal,
  updateShowPreActivationModal,
  onChargeAndActivateListingResponse,
  onPreActivationModalSubmit,
}) => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const {
    properties: { currentProperty },
    user,
  } = useSelector((state) => state);

  const [isLoadingContracts, setIsLoadingContracts] = useState(false);
  const [contractErrorMessage, setContractErrorMessage] = useState(null);
  const [activationErrorMessage, setActivationErrorMessage] = useState(null);
  const [contracts, setContracts] = useState([]);

  const { listingAlias, singleFamilyContract } = currentProperty;

  const { mutate: activateListing } = useRentalManagerUserPropertyActivateWebApiHandler_activate({
    mutationKey: ACTIVATE_LISTING_MUTATION_KEY,
  });

  useEffect(() => {
    const mustSetActiveContract = singleFamilyContract.isRequired && !singleFamilyContract.isActive;

    if (mustSetActiveContract) {
      setIsLoadingContracts(true);
      dispatch(SingleFamilyEnterpriseActions.getContracts())
        .then((newContracts) => {
          if (mustSetActiveContract && newContracts.length === 0) {
            setContractErrorMessage(ErrorMessageConstants.SINGLE_FAMILY_NO_ACTIVE_CONTRACT);
          } else {
            setContracts(newContracts);
          }
        })
        .catch(() => setContractErrorMessage(ErrorMessageConstants.SINGLE_FAMILY_CANNOT_RETRIEVE_CONTRACTS))
        .finally(() => setIsLoadingContracts(false));
    }
  }, [singleFamilyContract]);

  const handleDefaultActivateListing = () => {
    activateListing(
      { getParams: { listingAlias } },
      {
        onSuccess: (response) => {
          PayAndPublishCSEvents.ActivateListing({ currentProperty, user });

          onChargeAndActivateListingResponse({
            success: true,
            response: ChargeAndActivateListing({
              activation: {
                dateActivatedTimestamp: response?.activatedMs,
                dateUpdatedTimestamp: response?.updatedMs,
                expirationDateTimestamp: response?.expirationMs,
                ...response,
              },
            }),
          });

          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
        },
        onError: (error) => {
          const newErrorMessage =
            error?.errorDetails?.listingActivationFailed && error?.errorDetails?.activationError?.message
              ? error.errorDetails.activationError.message
              : error?.message;
          setActivationErrorMessage(newErrorMessage);
        },
      },
    );
  };

  return (
    <div data-testid="PreActivationModalWrapper">
      {(activationErrorMessage || (contractErrorMessage && showPreActivationModal)) && (
        <Modal
          open={true}
          onOpenChange={(open) =>
            !open &&
            (() => {
              setContractErrorMessage(null);
              setActivationErrorMessage(null);
            })()
          }
          header={<Heading level={3}>Something went wrong</Heading>}
          body={
            <div data-testid="PreActivationModalWrapper-error">
              <Alert tone="critical">{activationErrorMessage || contractErrorMessage}</Alert>
            </div>
          }
        />
      )}
      {showPreActivationModal && !isLoadingContracts && (
        <PreActivationModal
          onClose={() => updateShowPreActivationModal(false)}
          onSubmit={onPreActivationModalSubmit || handleDefaultActivateListing}
          contracts={contracts}
        />
      )}
    </div>
  );
};

PaymentGateway.propTypes = propTypes;
PaymentGateway.defaultProps = defaultProps;

export default PaymentGateway;
